import { Suspense, type ReactElement } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "./components/ui/sidebar";
import { AppSidebar } from "./components/app-sidebar";
import { AppBreadcrumbs } from "./components/app-breadcrumbs";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { RealtimeProvider } from "./contexts/RealtimeContext";

const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const RootRedirect = lazyWithRetry(() =>
  import("./components/RootRedirect").then((m) => ({
    default: m.RootRedirect,
  })),
);

const UserDashboard = lazyWithRetry(() => import("./pages/user/Dashboard"));
const CoursesPage = lazyWithRetry(() => import("./pages/user/CoursesPage"));
const DanceGroupsPage = lazyWithRetry(
  () => import("./pages/user/DanceGroupsPage"),
);
const DanceGroupDetailPage = lazyWithRetry(
  () => import("./pages/user/DanceGroupDetailPage"),
);
const PackagesPage = lazyWithRetry(() => import("./pages/user/PackagesPage"));
const PrivateLessonsPage = lazyWithRetry(
  () => import("./pages/user/PrivateLessonsPage"),
);
const PairsPage = lazyWithRetry(() => import("./pages/user/PairsPage"));
const AccountPage = lazyWithRetry(() => import("./pages/user/AccountPage"));
const NotificationsPage = lazyWithRetry(
  () => import("./pages/user/NotificationsPage"),
);
const TransactionsPage = lazyWithRetry(
  () => import("./pages/user/TransactionsPage"),
);

/**
 * AAC App - Main Application Component
 * React SPA aplikácia pre správu tanečnej školy
 */
function App(): ReactElement {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <BrowserRouter>
          <Suspense
            fallback={
              <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/100">
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="mt-4 text-sm text-muted-foreground">
                    Načítavam aplikáciu...
                  </p>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path="login" element={<LoginPage />} />

              <Route
                element={
                  <ProtectedRoute>
                    <SidebarProvider>
                      <AppSidebar />
                      <SidebarInset>
                        <header className="flex h-14 sm:h-16 shrink-0 items-center gap-2 sm:gap-3 border-b bg-gradient-to-r from-background via-background to-muted/100 px-3 sm:px-4 py-2 backdrop-blur-sm">
                          <SidebarTrigger className="-ml-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 touch-manipulation" />
                          <AppBreadcrumbs className="min-w-0 flex-1 overflow-hidden" />
                        </header>
                        <div className="flex flex-1 flex-col gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-background via-background to-muted/100">
                          <Outlet />
                        </div>
                      </SidebarInset>
                    </SidebarProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<RootRedirect />} />

                <Route path="user/dashboard" element={<UserDashboard />} />
                <Route path="user/courses" element={<CoursesPage />} />
                <Route path="user/dance-groups" element={<DanceGroupsPage />} />
                <Route
                  path="user/dance-groups/:id"
                  element={<DanceGroupDetailPage />}
                />
                <Route path="user/packages" element={<PackagesPage />} />
                <Route
                  path="user/private-lessons"
                  element={<PrivateLessonsPage />}
                />
                <Route path="user/pairs" element={<PairsPage />} />
                <Route path="user/account" element={<AccountPage />} />
                <Route
                  path="user/notifications"
                  element={<NotificationsPage />}
                />
                <Route
                  path="user/transactions"
                  element={<TransactionsPage />}
                />

                <Route
                  path="admin/*"
                  element={<Navigate to="/user/dashboard" replace />}
                />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </RealtimeProvider>
    </AuthProvider>
  );
}

export default App;
